import { useNavigation } from '@react-navigation/native';
import { BaseText, theme } from 'aimo-ui';
import VehicleAddSection from 'components/account/vehicle/VehicleAddSection';
import SelectVehicleItem from 'components/park/SelectVehicleItem';
import AccountContext from 'context/AccountContext';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermitStore } from 'stores/permitStore';
import { useVehicleStore } from 'stores/vehicleStore';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { LicensePlate, Maybe, Vehicle } from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

interface SelectVehiclesModalContentProps {
  maxLicensePlates?: Maybe<number>;
  handleOnChange: (licensePlates: LicensePlate[]) => void;
}

const SelectVehiclesModalContent: React.FC<SelectVehiclesModalContentProps> = ({
  maxLicensePlates,
  handleOnChange,
}) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const {
    customer: { vehicles },
  } = useContext(AccountContext);
  const { resetVehicleStore } = useVehicleStore((state) => state);
  const [selectedLicensePlates, setSelectedLicensePlates] = React.useState<
    LicensePlate[]
  >(usePermitStore.getState().selectedLicensePlatesPermitRedeem);
  const [vehiclesSet, setVehiclesSet] = React.useState<boolean>(false);
  const allowOneVehicle = !!maxLicensePlates && maxLicensePlates === 1;
  const toggleVehicleSelection = (vehicle: Vehicle) => {
    setSelectedLicensePlates((prevSelectedLicensePlates) => {
      if (!allowOneVehicle) {
        const vehicleIndex = prevSelectedLicensePlates.findIndex(
          (selectedLicensePlate) =>
            selectedLicensePlate.plateText === vehicle.licensePlate?.plateText
        );
        if (vehicleIndex >= 0) {
          return prevSelectedLicensePlates.filter(
            (selectedLicensePlate) =>
              selectedLicensePlate.plateText !== vehicle.licensePlate?.plateText
          );
        }
        return [...prevSelectedLicensePlates, vehicle.licensePlate];
      }
      return [vehicle.licensePlate];
    });
    setVehiclesSet(true);
  };

  const handleAddVehicle = () => {
    useUiStore.getState().setSheetModal('');
    resetVehicleStore();
    navigate('addVehicleParking');
  };

  useEffect(() => {
    if (vehiclesSet) {
      handleOnChange(selectedLicensePlates);
    }
  }, [handleOnChange, selectedLicensePlates, vehiclesSet]);

  const isSelected = (vehicle: Vehicle): boolean => {
    return (
      selectedLicensePlates.findIndex((selectedLicensePlate) => {
        return (
          selectedLicensePlate.plateText === vehicle.licensePlate?.plateText
        );
      }) >= 0
    );
  };

  return (
    <>
      <BaseText color={theme.colors.gray300} center>
        {maxLicensePlates
          ? t('permit.access.maxLicensePlates', {
              count: maxLicensePlates,
            })
          : t('permit.access.anyNoOfLicensePlates')}
      </BaseText>
      {vehicles?.map((vehicle, index) => {
        return (
          <SelectVehicleItem
            isSelected={isSelected(vehicle)}
            key={`${vehicle.licensePlate?.plateText}-${index}`}
            vehicle={vehicle}
            onPress={toggleVehicleSelection}
            allowMultipleSelection={!allowOneVehicle}
            disabled={
              !allowOneVehicle &&
              !isSelected(vehicle) &&
              selectedLicensePlates.length >=
                (maxLicensePlates || Number.MAX_SAFE_INTEGER)
            }
          />
        );
      })}
      <ColumnWithMargins {...testIdentifiers('add-vehicle-list-item-button')}>
        <VehicleAddSection onPress={handleAddVehicle} style="LIST" />
      </ColumnWithMargins>
    </>
  );
};

export default React.memo(SelectVehiclesModalContent);
